import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment, { Moment } from 'moment-timezone'
import React from 'react'
import { DayPickerRangeController, FocusedInputShape } from 'react-dates'
import { fadeInAnimation } from 'src/animations'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import { validDateOr } from 'src/utils/date.utils'
import { css } from 'styled-components'

interface CalendarDayPickerRangeProps {
  isOutsideRange: (day: moment.Moment) => boolean
  onDatesChange: (arg: {
    startDate: Moment | null
    endDate: Moment | null
  }) => void
  onFocusChange: (focusedInput: FocusedInputShape | null) => void
  focusedInput: FocusedInputShape | null
  startDate: string
  endDate: string
  minimumRangeSize?: number
  momentLocal: typeof moment | typeof moment.utc
  initialMonth?: Moment
}

const CalendarDayPickerRange: React.FC<CalendarDayPickerRangeProps> = (
  props,
) => {
  return (
    <div
      css={css`
        border-radius: 6px;
        border: 1px solid ${themeConstants.colors.cta};

        .DayPicker {
          width: 100% !important;
          border-radius: 6px;
          overflow: hidden;
        }

        .CalendarMonth_caption {
          ${getFontCss(
            14,
            themeConstants.colors.cta,
            styleConstants.fontFamilies.medium,
          )};
          padding-bottom: 48px;
        }

        .CalendarDay {
          border: 1px solid ${styleConstants.colors.gray_medium};
          background-color: ${styleConstants.colors.gray_lightest};

          &:hover {
            background-color: ${themeConstants.colors.cta_dark};
            ${getFontCss(
              14,
              styleConstants.colors.white,
              styleConstants.fontFamilies.regular,
            )};
          }
        }

        .CalendarDay__blocked_out_of_range {
          ${getFontCss(
            14,
            styleConstants.colors.gray_dark,
            styleConstants.fontFamilies.regular,
          )};
          &:hover {
            background-color: ${styleConstants.colors.gray_lightest};
            ${getFontCss(
              14,
              styleConstants.colors.gray_dark,
              styleConstants.fontFamilies.regular,
            )};
          }
        }

        .CalendarDay__selected {
          background-color: ${themeConstants.colors.cta};
        }

        .CalendarDay__hovered_span,
        .CalendarDay__selected_span {
          background-color: ${themeConstants.colors.cta};
          opacity: 0.5;
          color: ${styleConstants.colors.white};
        }

        .DayPickerKeyboardShortcuts_buttonReset {
          display: none;
        }

        .DayPickerNavigation {
          position: absolute;
          top: 16px;
          padding: 0px 24px;
          display: flex;
          width: 100%;
          justify-content: space-between;
        }

        ${fadeInAnimation(true)}
      `}
    >
      <DayPickerRangeController
        key={'startDate'}
        onDatesChange={props.onDatesChange}
        onFocusChange={props.onFocusChange}
        focusedInput={props.focusedInput || 'startDate'}
        startDate={validDateOr(props.momentLocal(props.startDate, 'll'), null)}
        endDate={validDateOr(props.momentLocal(props.endDate, 'll'), null)}
        isOutsideRange={props.isOutsideRange}
        numberOfMonths={1}
        initialVisibleMonth={(): Moment =>
          props.initialMonth ??
          validDateOr(
            props.momentLocal(props.startDate, 'll'),
            validDateOr(
              props.momentLocal(props.endDate, 'll'),
              moment.utc().subtract(30, 'days'),
            ),
          )
        }
        navPrev={
          <SvgChevronLeft
            css={css`
              transform: scale(1.2);
            `}
          />
        }
        navNext={
          <SvgChevronRight
            css={css`
              transform: scale(1.2);
            `}
          />
        }
        minimumNights={props.minimumRangeSize ?? 1}
      />
    </div>
  )
}

export default React.memo(CalendarDayPickerRange)
